import type { CamelCasedUserProfileAggregate } from '@leagueplatform/web-common';

/**
 * Build hidden pre-chat fields for Salesforce from a League user profile.
 *
 * Maps profile data to the key names expected by Salesforce's prechatAPI.
 * Returns only profile-derived fields; platform and chattype are applied
 * separately by the hook.
 */
export const buildHiddenPrechatFieldsFromProfile = (
  profile: CamelCasedUserProfileAggregate,
): Record<string, unknown> => {
  const hidden: Record<string, unknown> = {};
  const {
    userId,
    email,
    phoneNumber,
    groups,
    userProfile: innerProfile,
  } = profile;

  const {
    firstName,
    lastName,
    preferredFirstName,
    locale,
    locations,
  }: {
    firstName?: string;
    lastName?: string;
    preferredFirstName?: string;
    locale?: string;
    locations?: Array<{
      isDefault?: boolean;
      province?: string;
      state?: string;
      country?: string;
    }>;
  } = (innerProfile ?? {}) as {
    firstName?: string;
    lastName?: string;
    preferredFirstName?: string;
    locale?: string;
    locations?: Array<{
      isDefault?: boolean;
      province?: string;
      state?: string;
      country?: string;
    }>;
  };

  if (firstName) {
    hidden.firstname = firstName;
  }

  if (lastName) {
    hidden.lastname = lastName;
  }

  if (preferredFirstName) {
    hidden.preferredfirstname = preferredFirstName;
  }

  if (phoneNumber) {
    hidden.phonenumber = phoneNumber;
  }

  if (userId) {
    hidden.memberId = userId;
  }

  if (email) {
    hidden.email = email;
  }

  const employerGroup = groups?.find(
    (g) => !g.suspended && g.groupType === 'employer',
  );
  const leagueGroupId = employerGroup?.groupId;
  if (leagueGroupId) {
    hidden.leaguegroupid = leagueGroupId;
  }

  // Map profile locale to the Salesforce language field; callers may override it.
  if (locale) {
    hidden.language = locale;
  }

  const defaultLocation =
    locations?.find((location) => location.isDefault) ?? locations?.[0];
  const provinceOrState = defaultLocation?.province ?? defaultLocation?.state;
  if (provinceOrState) {
    hidden.province = provinceOrState;
  }

  if (defaultLocation?.country) {
    hidden.country = defaultLocation.country;
  }

  return hidden;
};
